import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Button, Drawer, Form, Input, Space, Switch, Table, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { IconDelete } from './reportingIcons';
import { createCategoryLabelMap, patchCategoryLabelMap } from '../../../actions/reportingActions';

const { Text } = Typography;

function mappingsToRows(mappings = {}) {
    return Object.entries(mappings).map(([code, label]) => ({ code, label }));
}

function rowsToMappings(rows) {
    const out = {};
    for (const row of rows ?? []) {
        const code = String(row.code ?? '').trim();
        if (!code) continue;
        out[code] = String(row.label ?? code).trim() || code;
    }
    return out;
}

export default function CategoryLabelMapEditorDrawer({ open, onClose, editingRow }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [mappingRows, setMappingRows] = useState([]);

    const isEdit = Boolean(editingRow?.id);

    useEffect(() => {
        if (!open) return;
        if (editingRow) {
            form.setFieldsValue({
                dimensionKey: editingRow.dimensionKey,
                displayName: editingRow.displayName,
                enabled: editingRow.enabled ?? true,
            });
            setMappingRows(mappingsToRows(editingRow.mappings));
        } else {
            form.resetFields();
            form.setFieldsValue({ enabled: true });
            setMappingRows([{ code: '', label: '' }]);
        }
    }, [open, editingRow, form]);

    const handleSave = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }

        const mappings = rowsToMappings(mappingRows);
        if (Object.keys(mappings).length === 0) {
            message.warning(t('Add at least one code → label mapping'));
            return;
        }

        setSaving(true);
        const result = isEdit
            ? await dispatch(patchCategoryLabelMap(editingRow.id, {
                displayName: values.displayName,
                mappings,
                enabled: values.enabled ?? true,
            }))
            : await dispatch(createCategoryLabelMap({
                dimensionKey: values.dimensionKey?.trim(),
                displayName: values.displayName,
                mappings,
                enabled: values.enabled ?? true,
            }));
        setSaving(false);

        if (result) {
            message.success(t('Label map saved'));
            onClose(true);
        }
    };

    return (
        <Drawer
            title={isEdit ? t('Edit label map') : t('Add label map')}
            open={open}
            onClose={() => onClose(false)}
            width={520}
            destroyOnClose
            extra={(
                <Space>
                    <Button onClick={() => onClose(false)}>{t('Cancel')}</Button>
                    <Button type="primary" loading={saving} onClick={handleSave}>
                        {t('Save')}
                    </Button>
                </Space>
            )}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="dimensionKey"
                    label={t('Dimension key')}
                    rules={[{ required: true, message: t('Required') }]}
                    extra={t('Must match chart dimensionFilter (e.g. sex, ethnicity, status).')}
                >
                    <Input disabled={isEdit} placeholder="sex" />
                </Form.Item>
                <Form.Item name="displayName" label={t('Display name')}>
                    <Input placeholder={t('Sex (NHS person stated gender)')} />
                </Form.Item>
                <Form.Item name="enabled" label={t('Enabled')} valuePropName="checked">
                    <Switch />
                </Form.Item>
            </Form>

            <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('Code → label')}</Text>
            <Table
                size="small"
                pagination={false}
                dataSource={mappingRows.map((r, i) => ({ ...r, key: i }))}
                columns={[
                    {
                        title: t('Code'),
                        dataIndex: 'code',
                        render: (v, _, index) => (
                            <Input
                                value={v}
                                onChange={e => {
                                    const next = [...mappingRows];
                                    next[index] = { ...next[index], code: e.target.value };
                                    setMappingRows(next);
                                }}
                                placeholder="1"
                            />
                        ),
                    },
                    {
                        title: t('Label'),
                        dataIndex: 'label',
                        render: (v, _, index) => (
                            <Input
                                value={v}
                                onChange={e => {
                                    const next = [...mappingRows];
                                    next[index] = { ...next[index], label: e.target.value };
                                    setMappingRows(next);
                                }}
                                placeholder={t('Male')}
                            />
                        ),
                    },
                    {
                        title: '',
                        width: 48,
                        render: (_, __, index) => (
                            <Button
                                type="text"
                                danger
                                icon={<IconDelete />}
                                onClick={() => setMappingRows(rows => rows.filter((_, i) => i !== index))}
                            />
                        ),
                    },
                ]}
                footer={() => (
                    <Button
                        type="dashed"
                        block
                        icon={<PlusOutlined />}
                        onClick={() => setMappingRows(rows => [...rows, { code: '', label: '' }])}
                    >
                        {t('Add row')}
                    </Button>
                )}
            />
        </Drawer>
    );
}
